import type { RandomIndexPort } from '../../application';

/** Fills a buffer with cryptographically strong random values: `crypto.getRandomValues`. */
export type RandomFill = (buffer: Uint32Array<ArrayBuffer>) => Uint32Array<ArrayBuffer>;

const UINT32_RANGE = 2 ** 32;

/**
 * Draws an index uniformly from [0, bound) out of the platform's cryptographic generator — never
 * `Math.random`.
 *
 * A 32-bit value reduced modulo `bound` would favour the smallest indices whenever 2³² is not a
 * multiple of `bound`. So values from the incomplete last block are rejected and drawn again:
 * what remains is an exact multiple of `bound`, and every index is exactly as likely as the
 * others. For 2 048 words the block is complete and nothing is ever rejected; the rule is there
 * for any other size.
 */
export class CryptoRandomIndex implements RandomIndexPort {
  constructor(
    private readonly fill: RandomFill = (buffer) => globalThis.crypto.getRandomValues(buffer),
  ) {}

  uniformIndex(bound: number): number {
    if (!Number.isInteger(bound) || bound < 1 || bound > UINT32_RANGE) {
      throw new RangeError('The bound must be a whole number between 1 and 2^32');
    }
    const limit = UINT32_RANGE - (UINT32_RANGE % bound);
    const buffer = new Uint32Array(1);
    for (;;) {
      // A generator that hands back nothing is treated like a rejected value: draw again.
      const value = this.fill(buffer)[0] ?? UINT32_RANGE;
      if (value < limit) return value % bound;
    }
  }
}
